import { useTourStore } from '../shared/stores/tourStore'

export default function (tour) {
  const tourStore = useTourStore()

  return {
    dashboard: [
      {
        id: 'welcome',
        title: 'Welcome to VERIFY!',
        text: `This trial account lets you explore the main
               functionalities of the tool. Follow this short tutorial for a
               quick guided tour. </br></br>When you see this icon: 👉, perform the
               suggested action to continue.`,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'sidebar',
        title: 'Dashboard navigation',
        text: `Use the side menu to view and manage your resources. Here, you
               can access your buildings and districts, as well as resources
               shared by other users. <br></br> 👉 <b>Click "My Buildings"</b> to
               get started.`,
        attachTo: { element: '[data-tour="dashboard-menu"]', on: 'right' },
        canClickTarget: true,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
        ]
      }
    ],
    
    buildingsList: [
      {
        id: 'buildings_management',
        title: 'Buildings management',
        text: 'On this page, you can view and manage all your buildings.',
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'new-building',
        title: 'Adding a new building',
        text: 'To add a new building, click the "New building" button.',
        attachTo: { element: '[data-tour="new-item-button"]', on: 'bottom-start' },
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'managing-building',
        title: 'Managing a building',
        text: `From here, you can view building details, edit information, share
               it with other users, duplicate, or delete the building.
               <br></br> 👉 <b>Click "Details"</b> on any building to continue.`,
        attachTo: { element: '[data-tour="dynamic-table"]', on: 'bottom-end' },
        canClickTarget: true,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
        ]
      }
    ],
    
    buildingDetails: [
      {
        id: 'building_show',
        title: 'Building management',
        text: `This is the building's management page. It includes detailed 
               information about the building as well as its baseline and 
               upgrade configurations.`,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'building_panel',
        title: 'Building details',
        text: `This panel displays your building's geographical, construction, 
               and usage information. You can also edit or delete the building here.`,
        attachTo: { element: '[data-tour="building-panel"]', on: 'right' },
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'building_table',
        title: 'Scenario management',
        text: `Each building can have one or more scenarios.</br> A scenario is a
               representation of the building's state, before or after any 
               interventions take place. Manage them from the scenarios table.
               <br></br> 👉 <b>Click "Details"</b> on the baseline scenario to continue.`,
        attachTo: { element: '[data-tour="building-table"]', on: 'top-start' },
        canClickTarget: true,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
        ]
      }
    ],

    scenarioShow: [
      {
        id: 'scenario-design',
        title: 'Scenario design',
        text: `On this page, you can design and configure your scenario.
               A scenario includes electrical and thermal components and other
               related configurations that affect the energy behaviour of the 
               building.`,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'component-categories',
        title: 'Component categories',
        text: `Switch between different component types using the tabs.`,
        attachTo: { element: '[data-tour="scenario-tabs"]', on: 'top' },
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'add-new-component',
        title: 'Add new component',
        text: `To add a new component, click the "Add new" button.`,
        attachTo: { element: '[data-tour="new-button"]', on: 'right' },
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'component-card',
        title: 'Component card',
        text: `Each component appears as a card with options to edit, clone, 
               or remove it.`,
        attachTo: { element: '[data-tour="component-card"]', on: 'right' },
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'related-info',
        title: 'Other related information',
        text: `You can also manage grid connections, financial parameters, and 
               data sources such as operational time series.`,
        attachTo: { element: '[data-tour="scenario-tabs"]', on: 'top-end' },
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'back-to-building',
        title: 'Back to Building',
        text: `<br></br> 👉 <b>Click "Back to building"</b> 
               to continue.`,
        attachTo: { element: '[data-tour="back-button"]', on: 'right' },
        scrollTo: true,
        canClickTarget: true,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
        ]
      }
    ],

    buildingLca: [
      {
        id: 'comparative_lca',
        title: 'Comparative LCA & LCC',
        text: `Select up to two scenarios from the list to perform a comparative 
               LCA & LCC.`,
        attachTo: { element: '[data-tour="building-table"]', on: 'bottom' },
        scrollTo: false,
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'perform_lca',
        title: 'Perform LCA & LCC',
        text: `Run the comparative LCA & LCC by clicking the "Perform LCA" button.`,
        attachTo: { element: '[data-tour="perform-lca-button"]', on: 'left' },
        buttons: [
          { text: '<i class="material-icons">close</i>', action: () => tourStore.skipActiveTour(), classes: 'shepherd-button-skip' },
          { text: '← Back', secondary: true, action: () => tour.back() },
          { text: 'Next →', action: () => tour.next() }
        ]
      },
      {
        id: 'thank_you',
        title: 'Thank you!',
        text: `You've now seen the most important parts of VERIFY. Evaluating a 
               district works similarly. You can restart the tutorial for "My Account"
               tab in the dashboard. </br> Thank you for your attention and enjoy 
               exploring the tool!`,
        buttons: [
          { text: '← Back', secondary: true, action: () => tour.back() },
          {
            text: 'Finish',
            action: () => {
              tourStore.completeActiveTour()
              tourStore.complete()
            }
          }
        ]
      }
    ],
  }
}